// rowWindow — read the /api/ops completeness envelope and say plainly whether
// the rows on screen are the whole set.
//
// Every ops list endpoint used to return only `count` — the number of rows in
// the response. With a default page size of 500 and hooks that passed no limit,
// dash_ems_stock_levels served 500 of its 880 rows under a field named "count"
// and there was NOTHING on the wire to compare it against: truncation was
// undetectable by construction, and pages rendered confident totals over 57% of
// the data. The worker now sends `total` / `truncated` / `completeness`.
//
// The rule this module holds: a response that cannot PROVE it is whole is never
// rendered as whole. That includes a response with no envelope at all (an older
// worker, a cached payload) — the absence of the fields is precisely the state
// that used to read as an all-clear, so it resolves to "unknown", never to
// "complete".

/** The completeness envelope returned by every /api/ops list endpoint. */
export interface RowWindowFields {
  /** Rows in THIS response. */
  count?: number;
  /** Rows matching the same filter with no LIMIT/OFFSET; null when unreadable. */
  total?: number | null;
  truncated?: boolean;
  completeness?: "complete" | "truncated" | "unknown";
  limit?: number;
  offset?: number;
}

export type RowWindowState =
  /** No response yet — the page is showing skeletons, not an answer. */
  | { kind: "loading" }
  | { kind: "complete"; shown: number }
  | { kind: "truncated"; shown: number; total: number; missing: number }
  /** The row total could not be established. Treat as a defect, not as OK. */
  | { kind: "unknown"; shown: number };

function asCount(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) && v >= 0
    ? Math.trunc(v)
    : null;
}

/**
 * Resolve a response into its completeness state.
 *
 * Derived from the NUMBERS first and the `completeness` label second: a server
 * that labels itself "complete" while shipping fewer rows than it counted is
 * still truncated, and the numbers are the thing a reader can check.
 */
export function rowWindowState(
  response: RowWindowFields | null | undefined,
): RowWindowState {
  if (response === null || response === undefined) return { kind: "loading" };

  const shown = asCount(response.count);
  const total = asCount(response.total);

  // No usable count of what we received: nothing can be concluded.
  if (shown === null) return { kind: "unknown", shown: 0 };

  // No usable total — an older worker, a failed COUNT, or an unreadable value.
  if (total === null) return { kind: "unknown", shown };

  // More rows than the server says exist: the total is not trustworthy.
  if (shown > total) return { kind: "unknown", shown };

  if (shown < total) {
    return { kind: "truncated", shown, total, missing: total - shown };
  }
  return { kind: "complete", shown };
}

/** True when the rows on screen are not provably the whole set. */
export function isRowWindowSuspect(state: RowWindowState): boolean {
  return state.kind === "truncated" || state.kind === "unknown";
}

/**
 * The user-facing sentence, or null when there is nothing to say.
 *
 * `label` names the data in the reader's language ("EMS stock rows" reads
 * better than "dash_ems_stock_levels").
 */
export function rowWindowMessage(
  state: RowWindowState,
  label: string,
): string | null {
  if (state.kind === "truncated") {
    const pct = Math.round((state.missing / state.total) * 100);
    return (
      `Showing ${state.shown.toLocaleString()} of ${state.total.toLocaleString()} ${label} rows — ` +
      `${state.missing.toLocaleString()} rows (${pct}%) are not loaded. ` +
      `Every count and chart on this page is computed over the ${state.shown.toLocaleString()} rows shown, ` +
      `so treat them as a floor, not a total.`
    );
  }
  if (state.kind === "unknown") {
    return (
      `Ember could not confirm how many ${label} rows exist, so it cannot tell whether the ` +
      `${state.shown.toLocaleString()} rows loaded here are the whole set. ` +
      `Treat every figure on this page as unverified until this clears.`
    );
  }
  return null;
}

/**
 * Render a tile value that was computed over a possibly-partial set.
 *
 * A count over truncated rows is a FLOOR ("12+"); a count over an unverifiable
 * set is a question ("12?"). Neither is allowed to print as a bare number,
 * which is what a reader takes for a total.
 */
export function rowWindowTileValue(
  value: number | string,
  state: RowWindowState,
): string {
  if (state.kind === "truncated") return `${value}+`;
  if (state.kind === "unknown") return `${value}?`;
  return `${value}`;
}

/**
 * Accent for a tile computed over `state`. Amber is the established signal on
 * these pages for "this number is not the whole story" (see the SCBA unknown
 * work on EquipmentGear); it deliberately outranks a green all-clear.
 */
export function rowWindowAccent(
  state: RowWindowState,
  accentWhenClean: "green" | "red" | "amber" | "neutral",
): "green" | "red" | "amber" | "neutral" {
  if (!isRowWindowSuspect(state)) return accentWhenClean;
  // Never let a partial set render green.
  return accentWhenClean === "red" ? "red" : "amber";
}

// ── Multi-source pages ────────────────────────────────────────────────────────

export interface RowWindowSource {
  /** Human name for the data, e.g. "SCBA cylinders". */
  label: string;
  response: RowWindowFields | null | undefined;
}

export interface RowWindowGroup {
  /** "truncated" outranks "unknown" outranks "none". */
  severity: "none" | "unknown" | "truncated";
  /** One line per source that cannot prove it is whole. */
  lines: string[];
  /** Sources checked, excluding those still loading. */
  checked: number;
}

/**
 * Roll several ops responses into ONE notice.
 *
 * A page like Equipment & Gear reads seven tables; seven separate banners is
 * noise a reader learns to scroll past, and noise that gets ignored is the same
 * as no warning at all. Sources still in flight are skipped — a page that has
 * not loaded yet is not making a claim.
 */
export function rowWindowGroup(sources: RowWindowSource[]): RowWindowGroup {
  const lines: string[] = [];
  let severity: RowWindowGroup["severity"] = "none";
  let checked = 0;

  for (const { label, response } of sources) {
    const state = rowWindowState(response);
    if (state.kind === "loading") continue;
    checked += 1;
    if (state.kind === "truncated") {
      const pct = Math.round((state.missing / state.total) * 100);
      lines.push(
        `${label}: ${state.shown.toLocaleString()} of ${state.total.toLocaleString()} rows loaded — ` +
          `${state.missing.toLocaleString()} missing (${pct}%).`,
      );
      severity = "truncated";
    } else if (state.kind === "unknown") {
      lines.push(
        `${label}: row total unavailable — Ember cannot confirm the ${state.shown.toLocaleString()} rows loaded are the whole set.`,
      );
      if (severity === "none") severity = "unknown";
    }
  }

  return { severity, lines, checked };
}
